import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { format, subDays, addDays, isToday, isBefore, startOfDay } from 'date-fns';

interface DateSelectorProps {
  value: Date;
  onChange: (date: Date) => void;
  maxDaysBack?: number;
}

export function DateSelector({ value, onChange, maxDaysBack = 0 }: DateSelectorProps) {
  const today = startOfDay(new Date());
  const minDate = subDays(today, maxDaysBack);

  const canGoBack = useMemo(() => {
    const prevDay = subDays(value, 1);
    return !isBefore(prevDay, minDate);
  }, [value, minDate]);

  const canGoForward = useMemo(() => {
    const nextDay = addDays(value, 1);
    return !isBefore(today, nextDay);
  }, [value, today]);

  const isPastDate = !isToday(value);
  const isOutsideWindow = isBefore(value, minDate);

  const handlePrev = () => {
    if (canGoBack) {
      onChange(subDays(value, 1));
    }
  };

  const handleNext = () => {
    if (canGoForward) {
      onChange(addDays(value, 1));
    }
  };

  const handleToday = () => {
    onChange(today);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {/* Navigation buttons */}
        <div className="flex items-center bg-white border border-gray-200 rounded-lg shadow-sm">
          <button
            onClick={handlePrev}
            disabled={!canGoBack}
            className={cn(
              'p-2.5 rounded-l-lg transition-colors',
              'hover:bg-gray-50 active:bg-gray-100',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent'
            )}
            aria-label="Previous day"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>

          <div className="flex items-center gap-2 px-4 py-2 border-x border-gray-200 min-w-[180px] justify-center">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className={cn(
              'font-medium text-sm',
              isToday(value) ? 'text-primary-600' : 'text-gray-900'
            )}>
              {isToday(value) ? 'Today' : format(value, 'EEE, MMM d')}
            </span>
          </div>

          <button
            onClick={handleNext}
            disabled={!canGoForward}
            className={cn(
              'p-2.5 rounded-r-lg transition-colors',
              'hover:bg-gray-50 active:bg-gray-100',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent'
            )}
            aria-label="Next day"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Today button (when not on today) */}
        {!isToday(value) && (
          <button
            onClick={handleToday}
            className={cn(
              'px-3 py-2 text-sm font-medium rounded-lg transition-colors',
              'bg-primary-50 text-primary-700 hover:bg-primary-100'
            )}
          >
            Go to Today
          </button>
        )}
      </div>

      {/* Warning for past dates */}
      {isPastDate && (
        <div className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg text-sm',
          isOutsideWindow
            ? 'bg-amber-50 text-amber-800 border border-amber-200'
            : 'bg-blue-50 text-blue-700 border border-blue-200'
        )}>
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {isOutsideWindow ? (
            <span>
              Entries for this date need a Farm Technician's approval
            </span>
          ) : (
            <span>
              Entering data for <strong>{format(value, 'MMMM d')}</strong>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
